import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom"; // Add useLocation
import DynamicTabsPage from "../tabs/tabs";
import apiHelper from "../../utils/apiHelper";
import {
  EntityType,
  LeaseAgreementStatusTypesEnum,
  TenantService,
} from "@propertelligent/client-api";
import SimpleSpinner from "../../components/loader";
import CurrentTenantCardItem from "./CurrentTenantCardItem";
import StatusHeader from "../../components/TenantOnboard/component/StatusHeader/StatusHeader";

const CurrentTenantDetails2 = () => {
  const { id, unitId } = useParams();
  const [listingData, setListingData] = useState(null);
  const [showConfirmation, setShowConfirmation] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [tenantUnitId, setTenantUnitId] = useState<number | null>(null);
  const [leaseStatusType, setLeaseStatusType] = useState(null);
  const navigate = useNavigate();
  const [paymentRefreshTrigger, setPaymentRefreshTrigger] = useState(0);
  const location = useLocation(); // Add useLocation to access history state

  // Fetch current tenant data on component mount
  const fetchCurrentTenantDetails = (isSilent = false) => {
    if (!isSilent) setListingData(null);
    if (id) {
      apiHelper(TenantService.getApiTenant1, { showNotification: false }, id)
        .then((res) => {
          if (res?.data?.success) {
            setListingData(res.data.success);
            
            setTenantUnitId(res?.data?.success.unitId || null);
            setLeaseStatusType(res.data.success.leaseAgreementStatus);
            setShowConfirmation(null);
          }
          const root = document.getElementById(".tabs-body");
          if (root) root.scrollTop = 0;
        })
        .catch((err) => {
          console.error("Error fetching tenants:", err);
          setShowConfirmation(null);
        })
        .finally(() => {
          setLoading(false);
        });
    }
  };

  const handlePaymentAction = () => {
    fetchCurrentTenantDetails(true); // This updates the Card Item data
    setPaymentRefreshTrigger((prev) => prev + 1); // This updates the list if needed
  };

  useEffect(() => {
    fetchCurrentTenantDetails();
  }, [id, unitId]);

  const handlePaymentAdded = () => {
    fetchCurrentTenantDetails(true); // Refresh property header stats (e.g. Income Due)
    setPaymentRefreshTrigger((prev) => prev + 1); // Signal PaymentList to refresh
  };

  // Handle delete unit action
  const handleDelete = (id) => {
    if (isDeleting) return;

    setIsDeleting(true);
    apiHelper(
      TenantService.deleteApiTenant,
      {
        showNotification: true,
        successMessage: "Tenant Deleted Successfully",
      },
      id,
    )
      .then((res) => {
        if (res?.data?.success) {
          setShowConfirmation(null);
          // Navigate to the referrer page or fallback to /tenants
          const referrer = location.state?.from || "/tenants";
          navigate(referrer);
        }
      })
      .catch((err) => {
        console.error("Error deleting tenant:", err);
        setShowConfirmation(null);
      })
      .finally(() => {
        setIsDeleting(false);
      });
  };

  const tabs = [
    "Overview",
    "Service Requests",
    "Payments",
    "Leases",
    "Transactions",
    "Additional Details",
  ];

  const status =
    leaseStatusType === LeaseAgreementStatusTypesEnum.RenewalInProgess
      ? "renew"
      : leaseStatusType === LeaseAgreementStatusTypesEnum.MoveOutInProgress
        ? "moveout"
        : "";

  const handleActionClick = () => {
    localStorage.setItem("TenantType", "E Lease");

    if (leaseStatusType === LeaseAgreementStatusTypesEnum.MoveOutInProgress) {
      navigate(`/tenants/move-out/${listingData?.id}`);
    } else {
      navigate(`/tenants/renew-lease/${listingData?.id}`);
    }
  };

  if (loading) {
    return <SimpleSpinner myClassName={"Icon50"} isFullPage={true} />;
  }

  return (
    <div className="content-wrapper full-page-content">
      {listingData && !loading ? (
        <>
          <CurrentTenantCardItem
            className="property-card-item-single"
            listingData={listingData}
            IsFooter={true}
            showConfirmation={showConfirmation}
            setShowConfirmation={setShowConfirmation}
            handleDelete={handleDelete}
            isDeletingTenant={isDeleting}
            onPaymentAdded={handlePaymentAdded}
          />
        </>
      ) : (
        <>
          <SimpleSpinner />
        </>
      )}
      {status && (
        <StatusHeader
          role="landlord"
          status={status}
          onActionClick={handleActionClick}
        />
      )}

      <DynamicTabsPage
        tabs={tabs}
        defaultActiveTab="Overview"
        entityType={EntityType.Tenant}
        tenantUnitId={tenantUnitId}
        paymentRefreshTrigger={paymentRefreshTrigger}
        onPaymentAction={handlePaymentAction}
        expensesDueRaw={listingData?.formatted?.IncomeDueRaw}
        expensesDue={listingData?.formatted?.IncomeDue}
      />
    </div>
  );
};

export default CurrentTenantDetails2;
